import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// ── Bootstrap Icons ────────────────────────────────────────────────────
// AIMS draws its rail with Bootstrap Icons, so the portals do too. Pinned
// to the same version AIMS loads (see aims2.0/frontend/index.html).
//
// It has to go in as a <link> rather than fetched-and-injected the way the
// portal loads its webfonts: the @font-face inside this stylesheet points
// at `fonts/bootstrap-icons.woff2` RELATIVELY, so a copy of the CSS served
// from our own <style> tag would resolve that against our origin and every
// glyph would come out as an empty box.
//
// The portal forces its own family onto every element, but that cannot
// reach these: Bootstrap Icons sets `font-family: bootstrap-icons
// !important` on `.bi::before`, and a pseudo-element with higher
// specificity and !important outranks the portal's rule either way.
const BI_HREF = 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.css';
const BI_TAG = 'data-bootstrap-icons';

const ensureBootstrapIcons = () => {
  if (typeof document === 'undefined') return;
  if (document.querySelector(`link[${BI_TAG}]`)) return;
  const link = document.createElement('link');
  link.setAttribute(BI_TAG, '');
  link.rel = 'stylesheet';
  link.href = BI_HREF;
  document.head.appendChild(link);
};

// One Bootstrap glyph. `name` is the icon id without the `bi-` prefix,
// e.g. "speedometer2" — the names are listed at icons.getbootstrap.com.
export const BiIcon = ({ name, size = 16, color }) => {
  ensureBootstrapIcons();
  return (
    <Box
      component="i"
      className={`bi bi-${name}`}
      sx={{ fontSize: size, lineHeight: 1, display: 'block', color }}
    />
  );
};

// Small reusable building block for portal sidebars.
// `avatarSrc` (optional) renders an image inside the avatar circle
// instead of the initials — used by the Corporate Portal to show
// the uploaded client logo where the initial would normally appear.
export const SidebarUser = ({ initials, name, role, avatarColor = '#FFC81F', avatarSrc }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={{ p: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
  >
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        bgcolor: avatarSrc ? '#fff' : avatarColor,
        color: '#0B4239',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'MedicalPortalSans', system-ui, sans-serif",
        fontSize: 14,
        fontWeight: 700,
        flexShrink: 0,
        overflow: 'hidden',
        p: avatarSrc ? '4px' : 0,
      }}
    >
      {avatarSrc ? (
        <Box
          component="img"
          src={avatarSrc}
          alt={name}
          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      ) : (
        initials
      )}
    </Box>
    <Box>
      <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#fff', lineHeight: 1.3 }}>{name}</Typography>
      <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{role}</Typography>
    </Box>
  </Stack>
);

// GateBook's `RailGroup` — a quiet mono caption, no strip and no bar. The
// rows below it carry the colour now, so a loud divider competes with them.
export const SidebarLabel = ({ children }) => (
  <Typography
    sx={{
      px: 1.25,
      pt: 2,
      pb: 0.75,
      fontFamily: "'MedicalPortalMono', ui-monospace, monospace",
      fontWeight: 600,
      fontSize: 10,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: '#8FB0A5',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Typography>
);

// GateBook's `RailLink`, reproduced: a coloured icon tile, an uppercase
// label, and a count on the right. The current row is a tinted, BORDERED
// rounded rect — a shape, not just a colour — with a bar in the row's own
// colour on the outer edge.
//
// `icon` is either a React node or the NAME of a Bootstrap icon.
// `iconColor` is GateBook's `--ic`: every entry has its own hue, so the
// list is navigable before the labels are read, and the active marker
// picks that hue up instead of being one colour for every row.
//
// `activeFg` / `activeBg` still override the active pairing for a portal
// that wants a different one (the corporate rail passes gold).
export const SidebarNavItem = ({
  icon,
  iconColor,
  label,
  badge,
  active,
  onClick,
  activeFg = '#FFFFFF',
  activeBg = 'rgba(46,177,255,.16)',
  activeBorder = 'rgba(46,177,255,.35)',
}) => (
  <Box
    onClick={onClick}
    sx={{
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      px: 1.25,
      py: 0.75,
      mb: '3px',
      minHeight: 44,
      borderRadius: '10px',
      // Transparent in the resting state so the row does not move by a
      // pixel when it becomes current.
      border: `1px solid ${active ? activeBorder : 'transparent'}`,
      bgcolor: active ? activeBg : 'transparent',
      color: active ? activeFg : '#E2EDE8',
      cursor: 'pointer',
      userSelect: 'none',
      transition: 'background-color .12s ease',
      '&:hover': { bgcolor: active ? activeBg : 'rgba(255,255,255,.07)' },
      ...(active
        ? {
          '&::before': {
            content: '""',
            position: 'absolute',
            left: -12,
            top: 6,
            bottom: 6,
            width: 4,
            borderRadius: '0 4px 4px 0',
            bgcolor: iconColor || activeFg,
          },
        }
        : null),
    }}
  >
    {/* The tile is a neutral white wash; the GLYPH carries the colour.
        Tinting the tile itself turned a column of twelve into a column of
        twelve coloured blocks. */}
    <Box
      sx={{
        position: 'relative',
        width: 34,
        height: 34,
        flexShrink: 0,
        borderRadius: '9px',
        display: 'grid',
        placeItems: 'center',
        color: iconColor || 'inherit',
        bgcolor: 'rgba(255,255,255,.06)',
        filter: 'brightness(1.18) saturate(1.15)',
        fontSize: 19,
        '& .MuiSvgIcon-root': { fontSize: 19 },
      }}
    >
      {typeof icon === 'string' ? <BiIcon name={icon} size={19} /> : icon}
    </Box>

    <Typography
      noWrap
      sx={{
        flex: 1,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.9px',
        textTransform: 'uppercase',
        color: 'inherit',
      }}
    >
      {label}
    </Typography>

    {badge != null && (
      <Box
        component="span"
        sx={{
          fontFamily: "'MedicalPortalMono', ui-monospace, monospace",
          fontSize: 11,
          fontWeight: 600,
          px: 0.875,
          py: '1px',
          borderRadius: '20px',
          flexShrink: 0,
          // A zero is still worth showing — it says "nothing waiting",
          // which is different from "not counted" — but it should not
          // read as an alert, so it sits in the muted pairing.
          bgcolor: Number(badge) > 0 ? 'rgba(255,200,31,.2)' : 'rgba(255,255,255,.08)',
          color: Number(badge) > 0 ? '#FFD45C' : '#8FB0A5',
        }}
      >
        {badge}
      </Box>
    )}
  </Box>
);

// ── Collapsible group ─────────────────────────────────────────
// A parent row that opens to reveal its children, so the rail reads as
// categories rather than one long list.
//
// The parent wears the same "current" treatment as a leaf row (tinted,
// bordered, bar on the outer edge) whenever one of its children is the open
// page — otherwise collapsing the group would hide the only indication of
// where you are.
export const SidebarGroup = ({
  icon,
  iconColor,
  label,
  open,
  onToggle,
  active,
  children,
}) => (
  <Box sx={{ mb: open ? '6px' : 0 }}>
    <Box
      onClick={onToggle}
      role="button"
      tabIndex={0}
      aria-expanded={!!open}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.25,
        py: 0.75,
        mb: '3px',
        minHeight: 44,
        borderRadius: '10px',
        border: `1px solid ${active ? 'rgba(46,177,255,.35)' : 'transparent'}`,
        bgcolor: active ? 'rgba(46,177,255,.16)' : 'transparent',
        color: active ? '#FFFFFF' : '#E2EDE8',
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'background-color .12s ease',
        '&:hover': { bgcolor: active ? 'rgba(46,177,255,.16)' : 'rgba(255,255,255,.07)' },
        ...(active
          ? {
            '&::before': {
              content: '""',
              position: 'absolute',
              left: -12,
              top: 6,
              bottom: 6,
              width: '4px',
              borderRadius: '0 4px 4px 0',
              bgcolor: iconColor || '#FFC81F',
            },
          }
          : null),
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          flexShrink: 0,
          borderRadius: '9px',
          display: 'grid',
          placeItems: 'center',
          color: iconColor || 'inherit',
          bgcolor: 'rgba(255,255,255,.06)',
          filter: 'brightness(1.18) saturate(1.15)',
          fontSize: 19,
        }}
      >
        {typeof icon === 'string' ? <BiIcon name={icon} size={19} /> : icon}
      </Box>

      <Typography
        noWrap
        sx={{
          flex: 1,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.9px',
          textTransform: 'uppercase',
          color: 'inherit',
        }}
      >
        {label}
      </Typography>

      <Box
        sx={{
          color: 'rgba(255,255,255,.45)',
          display: 'flex',
          transform: open ? 'rotate(180deg)' : 'rotate(0)',
          transition: 'transform .18s',
        }}
      >
        <BiIcon name="chevron-down" size={12} />
      </Box>
    </Box>

    {/* The children sit on a sunken panel so the group reads as one block
        rather than as more top-level rows that happen to be indented. */}
    {open && (
      <Box
        sx={{
          bgcolor: 'rgba(0,0,0,.18)',
          borderRadius: '10px',
          px: '6px',
          py: '6px',
        }}
      >
        {children}
      </Box>
    )}
  </Box>
);

// A row inside a group. Title case and a bare glyph — no tile — so it reads
// as subordinate to the uppercase, tiled parent above it.
export const SidebarSubItem = ({ icon, iconColor, label, active, onClick }) => (
  <Box
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      px: 1,
      py: '7px',
      minHeight: 34,
      borderRadius: '8px',
      cursor: 'pointer',
      userSelect: 'none',
      color: active ? '#FFC81F' : 'rgba(226,237,232,.92)',
      fontWeight: active ? 600 : 500,
      bgcolor: active ? 'rgba(255,200,31,.14)' : 'transparent',
      transition: 'background-color .12s ease, color .12s ease',
      '&:hover': { bgcolor: active ? 'rgba(255,200,31,.14)' : 'rgba(255,255,255,.07)' },
    }}
  >
    <Box
      sx={{
        width: 22,
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        color: active ? '#FFC81F' : (iconColor || 'inherit'),
        filter: 'brightness(1.18) saturate(1.15)',
      }}
    >
      {typeof icon === 'string' ? <BiIcon name={icon} size={15} /> : icon}
    </Box>
    <Typography noWrap sx={{ flex: 1, fontSize: 12.5, color: 'inherit', lineHeight: 1.3 }}>
      {label}
    </Typography>
  </Box>
);

